import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, CalendarClock, Hourglass, ChevronDown, ClipboardList } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null);

export function AlertsPanel() {
  const [a, setA] = useState(null);
  const [open, setOpen] = useState("overdue");
  useEffect(() => { api.get("/dashboard/alerts").then((r) => { setA(r.data); if (r.data.needs_details?.length) setOpen("needs_details"); }).catch(() => setA({ needs_details: [], overdue: [], due_soon: [], stale: [] })); }, []);
  if (!a) return null;
  const groups = [
    ["needs_details", "Field pickups — details needed", a.needs_details || [], ClipboardList, "text-violet-700 bg-violet-100", "border-l-violet-500"],
    ["overdue", "Overdue", a.overdue, AlertTriangle, "text-rose-700 bg-rose-100", "border-l-rose-500"],
    ["due_soon", "Due in 3 days", a.due_soon, CalendarClock, "text-amber-700 bg-amber-100", "border-l-amber-400"],
    ["stale", "No attempt in 5+ days", a.stale, Hourglass, "text-slate-700 bg-slate-200", "border-l-slate-400"],
  ];
  const total = groups.reduce((n, g) => n + g[2].length, 0);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="alerts-panel">
      <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
        <div>
          <h3 className="text-sm font-display font-bold tracking-tight">Needs attention</h3>
          <div className="text-xs text-slate-500">{total ? `${total} open job${total === 1 ? "" : "s"} flagged · admins get this as a 7am digest` : "All clear — nothing overdue or stale"}</div>
        </div>
        {total > 0 && <span className="h-8 min-w-8 px-2 rounded-full bg-rose-600 text-white text-sm font-bold grid place-items-center" data-testid="alerts-total">{total}</span>}
      </div>
      {groups.map(([key, label, items, Icon, tone, border]) => (
        <div key={key} className="border-b border-slate-100 last:border-0">
          <button type="button" onClick={() => setOpen(open === key ? null : key)} className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-slate-50" data-testid={`alerts-group-${key}`}>
            <span className={`h-7 w-7 rounded-md grid place-items-center ${tone}`}><Icon className="h-3.5 w-3.5" /></span>
            <span className="text-sm font-medium flex-1">{label}</span>
            <span className="text-xs font-mono text-slate-500">{items.length}</span>
            <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open === key ? "rotate-180" : ""}`} />
          </button>
          {open === key && items.length > 0 && (
            <ul className="pb-2">
              {items.slice(0, 8).map((j) => (
                <li key={j.id}>
                  <Link to={`/jobs/${j.id}`} className={`mx-3 my-1 block rounded-md border-l-[3px] ${border} bg-slate-50 hover:bg-[#eef5fc] px-3 py-2`} data-testid={`alert-job-${j.job_number}`}>
                    <div className="flex items-center gap-2 text-xs"><span className="font-mono text-[#1f5fa8]">{j.job_number}</span><span className="text-slate-500 truncate">{j.server_name || "Unassigned"}</span><span className="ml-auto text-slate-500">{j.due_date ? `Due ${fmt(j.due_date)}` : j.last_attempt_at ? `Last ${fmt(j.last_attempt_at)}` : "No attempts"}</span></div>
                    <div className="text-sm font-medium text-slate-900 truncate">{j.recipient_name}</div>
                  </Link>
                </li>
              ))}
              {items.length > 8 && <li className="px-5 py-1 text-xs text-slate-500">…and {items.length - 8} more in <Link to="/jobs" className="sm-link">Jobs</Link></li>}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
